import { action, observable, runInAction } from "mobx";
import { RegistrationFormData } from "@data-types/membership/registration";
import { registrationValidationSingle } from "@utils/membership/registrationValidation";
import { MainRegistrationStore } from "../MainRegistrationStore";
import { PlatformRegistrationApiService } from "@api-services/platform";
import {
	getRedirectOriginUrl,
	isCapacitorPlatform,
} from "@utils/specific/capacitor/helpers";
import { DateTime } from "luxon";
import { DeviceDetector } from "@utils";
import { BankIdApiService } from "@api-services/integrations/BankIdApiService";
import { validateBankIdProcessResponse } from "@utils/lookups/validators";
import { BankIdFormData } from "@api-types/integrations";
import { localizationService } from "@state/services";
import { analyticsTracker } from "@utils/analytics";
import { RegistrationTypeEnum } from "@data-types/analytics-tracker/RegistrationTypeEnum";
import { RegistrationMethod } from "@data-types/user/RegistrationMethod";
import {
	BANKID_QUERY_PARAMS,
	BankIdStorageKey,
} from "@utils/specific/bankid/bankIdConstants";
import {
	isBankIdRedirectCancel,
	hasBankIdRedirectFailure,
	bankIdRedirectMessageKey,
} from "@utils/specific/bankid/bankIdRedirect";
import {
	captureRegistrationCampaign,
	startRegistrationSession,
} from "@utils/analytics/registrationCampaignStorage";

export default class BankIdRegistrationStore extends MainRegistrationStore {
	@observable formData: BankIdFormData | null;
	@observable registrationMethod: RegistrationMethod | null = null;

	@action.bound
	setRegistrationMethod(method: RegistrationMethod) {
		this.registrationMethod = method;
	}

	constructor() {
		super();
	}

	@action.bound
	async onInitialize() {
		captureRegistrationCampaign();

		if (App.state.rootStore.isIpRestricted) {
			return;
		}

		startRegistrationSession();

		analyticsTracker.trackRegistrationStarted(
			RegistrationTypeEnum.BankIdRegistration
		);

		await this.bankIdParamProcess();

		this.fetchLookups();
	}

	@action.bound
	async getUserInfo(sessionId: string): Promise<void> {
		this.loader.suspend();

		const data = {
			sessionId,
			redirectUrl: getRedirectOriginUrl(window.location.pathname, true),
		};

		try {
			const response = await BankIdApiService.getUserAdditionalInfo(data);

			let formData: BankIdFormData = {};

			if (response.firstName && response.lastName) {
				formData.firstName = response.firstName;
				formData.lastName = response.lastName;
			}

			if (response.pinNumber) {
				formData.personalIdentificator = response.pinNumber;
			}

			if (response.birthDate) {
				const datetime = DateTime.fromISO(response.birthDate);

				if (DeviceDetector.isMobileTheme) {
					((formData.day = datetime.get("day").toString()),
						(formData.month = datetime.get("month").toString()),
						(formData.year = datetime.get("year").toString()));
				} else {
					formData.dob = datetime.toFormat("yyyy-MM-dd");
				}
			}

			runInAction(() => {
				this.formData = formData;

				sessionStorage.setItem(
					BankIdStorageKey.AdditionalUserInfo,
					JSON.stringify(formData)
				);

				sessionStorage.setItem(
					BankIdStorageKey.PrefilledFieldsDisabled,
					JSON.stringify(true)
				);
			});
		} catch (error) {
			console.error(error);

			runInAction(() => {
				this.registrationMethod = null;
			});

			let errorMessage: string;

			switch (error?.data?.errorCode) {
				case 400921:
					errorMessage =
						"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_NOT_VALID";
					break;
				case 400922:
					errorMessage =
						"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_FAILED";
					break;
				default:
					errorMessage = "registration:GENERIC_ERROR";
					break;
			}

			App.state.rootStore.notificationStore.error(
				localizationService.t(errorMessage)
			);

			this.removeBankIdQueryParams();
		} finally {
			this.loader.resume();
		}
	}

	bankIdParamProcess = async () => {
		const params = new URLSearchParams(window.location.search);

		const cancel = isBankIdRedirectCancel();
		const failureWithoutSession =
			hasBankIdRedirectFailure() && !params.get("sessionId");

		if (cancel || failureWithoutSession) {
			this.removeBankIdProcessLocalStorageKeys();

			runInAction(() => {
				this.registrationMethod = null;
			});

			App.state.rootStore.notificationStore.error(
				localizationService.t(bankIdRedirectMessageKey(cancel))
			);

			this.removeBankIdQueryParams();

			return;
		}

		if (
			params.get("sessionId") ||
			sessionStorage.getItem(BankIdStorageKey.AdditionalUserInfo)
		) {
			runInAction(() => {
				this.registrationMethod = RegistrationMethod.BankId;
			});
		}

		if (params.get("sessionId")) {
			sessionStorage.setItem(
				BankIdStorageKey.RegistrationSessionId,
				params.get("sessionId") as string
			);
		}

		if (params.get("sessionId")) {
			const paramsObject = {
				sessionId: params.get("sessionId"),
			};
			if (sessionStorage.getItem(BankIdStorageKey.AdditionalUserInfo)) {
				runInAction(() => {
					this.formData = JSON.parse(
						sessionStorage.getItem(
							BankIdStorageKey.AdditionalUserInfo
						) as string
					);
				});
			} else if (paramsObject.sessionId) {
				await this.getUserInfo(paramsObject.sessionId);
			}
		}
	};

	@action.bound
	async initializeBankIdProcess(): Promise<void> {
		this.loader.suspend();

		this.removeBankIdProcessLocalStorageKeys();

		const successRedirectUrl = getRedirectOriginUrl(
			window.location.pathname,
			true
		);
		const failRedirectUrl = getRedirectOriginUrl(
			window.location.pathname + "?failedAuth=true",
			true
		);

		try {
			const response = await BankIdApiService.getBankIdWidgetInfo(
				"same-device",
				successRedirectUrl,
				failRedirectUrl
			);
			const isValid = validateBankIdProcessResponse(
				response,
				"bankIdRegistrationStore"
			);

			if (!isValid) {
				App.state.rootStore.notificationStore.error(
					localizationService.t(
						"specific:MEMBERSHIP.REGISTRATION.BANKID.AUTH_NOT_VALID"
					)
				);
				this.loader.resume();
				return;
			}

			if (response.redirectUrl) {
				window.location.replace(response.redirectUrl);
			}

			if (isCapacitorPlatform()) {
				this.loader.resume();
			}
		} catch (err) {
			this.loader.resume();
			console.error(err);
			let errorMessage: string;

			if (err.data.errorCode == 400920) {
				errorMessage =
					"specific:MEMBERSHIP.REGISTRATION.BANKID.WIDGET_INIT_FAILED";
			} else {
				errorMessage = "registration:GENERIC_ERROR";
			}

			App.state.rootStore.notificationStore.error(
				localizationService.t(errorMessage)
			);
		}
	}

	@action.bound
	async register(
		formData: RegistrationFormData,
		affiliateCode: string | null
	) {
		try {
			this.loader.suspend();

			const body = this.prepareRegistrationFormData(
				formData,
				affiliateCode
			);

			const additionalBankIdUserInfo = sessionStorage.getItem(
				BankIdStorageKey.AdditionalUserInfo
			);

			if (additionalBankIdUserInfo) {
				body.isExternalProviderRegistration = true;

				const bankIdSessionId = sessionStorage.getItem(
					BankIdStorageKey.RegistrationSessionId
				);
				if (bankIdSessionId) {
					body.bankIdSessionId = bankIdSessionId;
				}

				await PlatformRegistrationApiService.registerSeUser(body);
			} else {
				this.addCampaignAttribution(body);

				await PlatformRegistrationApiService.registerUser(body);
			}
			this.isRegistrationSuccessful = true;
			analyticsTracker.trackRegistrationFormCompleted();
		} catch (error) {
			console.error(error);

			registrationValidationSingle(error?.data);
		} finally {
			this.loader.resume();
			document.querySelector("main")?.scrollTo(0, 0);
		}
	}

	@action.bound
	setIsRegistrationSuccessful(isSuccessful: boolean) {
		this.isRegistrationSuccessful = isSuccessful;
	}

	removeBankIdProcessLocalStorageKeys() {
		//clear session storage keys after successful registration/errors
		sessionStorage.removeItem(BankIdStorageKey.AdditionalUserInfo);
		sessionStorage.removeItem(BankIdStorageKey.PrefilledFieldsDisabled);
		sessionStorage.removeItem(BankIdStorageKey.RegistrationSessionId);
	}

	removeBankIdQueryParams() {
		const params = new URLSearchParams(window.location.search);
		BANKID_QUERY_PARAMS.forEach((param) => params.delete(param));

		const query = params.toString();

		window.history.replaceState(
			{},
			"",
			window.location.pathname + (query ? `?${query}` : "")
		);
	}

	@action.bound
	onDispose() {
		this.currentStepReactionDisposer?.();
		this.currentStep = 1;
		this.isRegistrationSuccessful = null;
		this.registrationMethod = null;

		//lookups
		this.titleData = [];
		this.countryData = [];
		this.languageData = [];
		this.countryRegionsData = [];
		this.gamingLimitsData = null;

		sessionStorage.removeItem(BankIdStorageKey.PrefilledFieldsDisabled);
	}
}
